import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import saunaImg from "@/assets/do-sauna.webp";
import barImg from "@/assets/eat-bar.webp";
import cultureImg from "@/assets/do-suomenlinna.webp";
import cafeImg from "@/assets/eat-cafe.webp";
import beachImg from "@/assets/category-beach.webp";
import dineImg from "@/assets/eat-finedining.webp";

export type VibeCategory = {
  id: string;
  label: string;
  image_url: string;
  to: "/eat" | "/do";
  lane?: string;
};

export type SiteSettings = {
  hero_images: string[];
  /** Optional mobile-only hero set; empty = reuse `hero_images`. */
  hero_images_mobile: string[];
  hero_badge_text: string;
  hero_headline_primary: string;
  hero_headline_secondary: string;
  hero_subheadline: string;
  ai_prompts: string[];
  aurora_title: string;
  aurora_subtitle: string;
  aurora_cta_label: string;
  aurora_image_url: string;
  hero_stat_spots: string;
  hero_stat_google_rating: string;
  hero_stat_cities: string;
  hero_stat_spots_label: string;
  hero_stat_google_rating_label: string;
  hero_stat_cities_label: string;
  hero_stat_cities_icon_url: string;
  bokun_channel_uuid: string;
  bokun_product_list_id: string;
  vibe_categories: VibeCategory[];
  /** Admin-managed homepage section toggles + titles (see src/lib/home-sections.ts) */
  home_sections: Array<{ key: string; enabled: boolean; eyebrow_en?: string; eyebrow_fi?: string; title_en?: string; title_fi?: string }>;
  // Place detail page copy (admin-editable, overrides i18n defaults; empty = use default)
  place_tagline_eat: string;
  place_tagline_do: string;
  place_tagline_happening: string;
  place_promo_title_eat: string;
  place_promo_title_do: string;
  place_promo_title_happening: string;
  place_promo_body: string;
  place_search_eat: string;
  place_search_do: string;
  place_search_happening: string;
  place_cta_title: string;
  place_cta_body: string;
};

export const DEFAULT_VIBE_CATEGORIES: VibeCategory[] = [
  { id: "sauna", label: "Sauna & Sea", image_url: saunaImg, to: "/do", lane: "sauna" },
  { id: "nightlife", label: "Drinks & Nightlife", image_url: barImg, to: "/do", lane: "nightlife" },
  { id: "culture", label: "Sights & Culture", image_url: cultureImg, to: "/do", lane: "culture" },
  { id: "cafes", label: "Cafés & Breakfast", image_url: cafeImg, to: "/do", lane: "cafes" },
  { id: "beaches", label: "Beaches", image_url: beachImg, to: "/do", lane: "nature" },
  { id: "dine", label: "Eat & Dine", image_url: dineImg, to: "/eat" },
];

export const DEFAULT_SETTINGS: SiteSettings = {
  hero_images: [],
  hero_images_mobile: [],
  hero_badge_text: "Unlock Finland",
  hero_headline_primary: "",
  hero_headline_secondary: "",
  hero_subheadline:
    "Find places, restaurants and things to do — handpicked by locals, ready to unlock in seconds.",
  ai_prompts: [
    "whatAI — best sushi in Helsinki tonight",
    "whatAI — sauna with sea view",
    "whatAI — what's happening this weekend",
  ],
  aurora_title: "unlock the city, one whisper at a time",
  aurora_subtitle:
    "Local secrets, curated drops, and AI that knows what you'll love — all in one place.",
  aurora_cta_label: "discover what else",
  aurora_image_url: "",
  hero_stat_spots: "",
  hero_stat_google_rating: "",
  hero_stat_cities: "",
  hero_stat_spots_label: "",
  hero_stat_google_rating_label: "",
  hero_stat_cities_label: "",
  hero_stat_cities_icon_url: "",
  bokun_channel_uuid: "",
  bokun_product_list_id: "",
  vibe_categories: DEFAULT_VIBE_CATEGORIES,
  home_sections: [],
  place_tagline_eat: "",
  place_tagline_do: "",
  place_tagline_happening: "",
  place_promo_title_eat: "",
  place_promo_title_do: "",
  place_promo_title_happening: "",
  place_promo_body: "",
  place_search_eat: "",
  place_search_do: "",
  place_search_happening: "",
  place_cta_title: "",
  place_cta_body: "",
};


export function usePublicSettings(initial?: Partial<SiteSettings> | null): SiteSettings {
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: ["public-site-settings"],
    queryFn: async (): Promise<Partial<SiteSettings>> => {
      const { data, error } = await supabase.from("site_settings").select("key, value");
      if (error) throw error;
      const obj: any = {};
      for (const row of data ?? []) obj[(row as any).key] = (row as any).value;
      return obj as Partial<SiteSettings>;
    },
    // Server-rendered copy stays on screen until fresh data arrives — no
    // flash back to the built-in defaults during hydration.
    initialData: initial ? (initial as Partial<SiteSettings>) : undefined,
    staleTime: 60_000,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    const invalidate = () => {
      queryClient.invalidateQueries({ queryKey: ["public-site-settings"] });
    };

    // Note: site_settings is intentionally NOT in the Realtime publication —
    // private (is_public=false) rows must never be broadcast. Updates rely on
    // refetchOnWindowFocus + a same-browser BroadcastChannel.
    let bc: BroadcastChannel | null = null;
    try {
      bc = new BroadcastChannel("site-settings");
      bc.onmessage = invalidate;
    } catch {
      // BroadcastChannel not available — settings still refresh on focus
    }

    return () => {
      bc?.close();
    };
  }, [queryClient]);

  const merged = { ...DEFAULT_SETTINGS, ...(initial ?? {}), ...(data ?? {}) };
  // Vibe categories: if admin saved an empty array, treat as "use defaults"
  if (!Array.isArray(merged.vibe_categories) || merged.vibe_categories.length === 0) {
    merged.vibe_categories = DEFAULT_VIBE_CATEGORIES;
  }
  return merged;
}
